import { Component, HostBinding, ViewChild, ViewEncapsulation } from '@angular/core';
import { NgForm } from '@angular/forms';
import * as _ from 'underscore';
import { Endpoint, HelpIndex, MessageType, SettingsType } from '../../../../models/enums';
import { AutoProjectCreation, AvailableProjectSetupOptions, Preferences, PreferencesViewModel } from '../../../../models/preferences';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { Timezone } from '../../../../models/timezone';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { TimezoneModalComponent } from '../../timezone-preferences/timezone-modal.component';
import { Permissions, ObjectActionListType, ActionType } from '../../../../models/permission';
import { isArray } from '../../../../models/utils';

@Component({
    standalone: false,
    selector: 'res-preferences',
    templateUrl: './preferences-settings.component.html',
    styleUrls: ['./preferences-settings.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class PreferencesComponent implements SettingsNavigationContentComponent {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild('contentForm', { static: true }) contentForm: NgForm;

    settingsType: SettingsType;

    handle = '';
    timeZonesList: Array<Timezone> = [];
    allTimezoneList: Array<Timezone> = [];
    previousTimezone: Timezone;
    prevZoneSelected: Timezone;
    hasPermission: boolean;
    isReview: boolean;

    automationId = 'preferences';
    helpIndex: any;
    isHelpIndex = true;

    isBusy = false;
    isBusyMessage = 'Loading ...';
    validChanges = true;

    preferences: Preferences = new Preferences();
    viewModel: PreferencesViewModel = new PreferencesViewModel(this.preferences);

    checkboxErrorMessage = 'Please select at least one Project Setup Option.';
    radioButtonErrorMessage = 'Please indicate which Project Setup Option should be selected by default.';
    checkboxValidation = false;
    radioButtonValidation = false;

    isDROnlyOption = false;
    isReefAndRelativityOption = false;
    isReefAndRROption = false;

    tooltip = 'Please select the corresponding Project Setup Option';

    availableProjectSetupOptions = {
        [AvailableProjectSetupOptions.DIGITAL_REEF_ONLY]: false,
        [AvailableProjectSetupOptions.DIGITAL_REEF_AND_RELATIVITY]: false,
        [AvailableProjectSetupOptions.DIGITAL_REEF_AND_REEF_REVIEW]: false
    }

    modes = this.commonService.getEnumNames(AvailableProjectSetupOptions);
    originalAvailableProjectSetupOptions = {};

    get isDirty(): boolean {
        if (this.contentForm && this.viewModel.isDirty()) {
            return this.contentForm.dirty;
        }
        return false;
    }
    set isDirty(value: boolean) {
        if (this.contentForm && this.contentForm.control) {
            if (value) {
                this.contentForm.control.markAsDirty();
            } else {
                this.contentForm.control.markAsPristine();
            }
        }
    }

    constructor(private commonService: CommonService,
        private restService: RestService) {
    }

    selectedTreeItemChanged(node: GenericTreeNode, projectHandle?: string, settingsType?: SettingsType): void {
        this.settingsType = settingsType;
        this.viewModel = new PreferencesViewModel(new Preferences());

        if (settingsType === SettingsType.project) {
            this.isReview = this.commonService.isReview;
            this.handle = projectHandle;
            this.helpIndex = HelpIndex.myedisc_case_preferences_dlg;
            this.hasPermission = Permissions.checkUserPermission(ObjectActionListType.PROJECT_PREFERENCES, ActionType.CREATE);
            this.listDRTimezones();
        } else if (settingsType === SettingsType.organization) {
            this.hasPermission = Permissions.checkUserPermission(ObjectActionListType.REEF_EXPRESS_PREFERENCES, ActionType.CREATE);
            this.isHelpIndex = false;

            this.getReefExpressSettings();
        }
    }

    listDRTimezones(): void {
        this.isBusy = true;
        this.timeZonesList = [];
        this.viewModel.selectedTimezone = null;
        this.restService.post(Endpoint.realmManager_listDRTimezones, {}, true).then(response => {
            this.listDRTimezonesCompleted(response);
        }, error => {
            this.isBusy = false;
        });
    }

    listDRTimezonesCompleted(response): void {
        if (response && response.timezones) {
            response.timezones.forEach(entry => {
                if (this.timeZonesList.length > 0 && this.timeZonesList[0].id === entry.id) {
                    this.timeZonesList.splice(0, 1);
                }
                this.timeZonesList.push(new Timezone(entry));
            });
        }

        const spacingOffsets = [-4.5, 3.5, 12.5];
        spacingOffsets.forEach(spacingOffset => {
            this.timeZonesList.push(new Timezone({
                description: '',
                id: '',
                offsetInfo: {
                    hours: spacingOffset,
                    mins: 0,
                    negative: false
                }
            }));
        });

        this.sortTimezone();

        this.timeZonesList.push(new Timezone({
            description: 'Other...',
            id: 'Other'
        }));

        this.restService.post(Endpoint.realmManager_listAllTimezones, {}, true).then(allTimeZones_response => {
            this.listAllTimezones(allTimeZones_response);
        }, error => {
            this.isBusy = false;
        });
    }

    listAllTimezones(response): void {
        this.allTimezoneList = [];
        if (response && response.timezones) {
            response.timezones.forEach(entry => {
                this.allTimezoneList.push(new Timezone(entry));
            });
        }
        this.restService.post(Endpoint.projectManager_getPreference, { ownerId: this.handle }).then(preferences => {
            this.getPreferenceCompleted(preferences);
        }, error => {
            this.isBusy = false;
        });

    }

    getPreferenceCompleted(response): void {
        if (response && response.preferences) {
            response.preferences.lastAccessTimeForArchival = { value: response.preferences.lastAccessTimeForArchival };
            this.viewModel = new PreferencesViewModel(response.preferences);

            if (!this.viewModel.selectedTimezone) {
                const searchTimeZone = this.searchTimezones(this.timeZonesList, this.viewModel.model.drTimezone);
                this.viewModel.selectedTimezone = this.timeZonesList[searchTimeZone.position];
                if (searchTimeZone.found === undefined || searchTimeZone == null) {
                    const prefTz = this.searchTimezones(this.allTimezoneList, this.viewModel.model.drTimezone);
                    if (prefTz.found !== undefined || prefTz != null) {
                        this.timeZonesList.splice(0, 0, prefTz.found);
                        this.viewModel.selectedTimezone = this.allTimezoneList[prefTz.position];
                    }
                }
            }
            this.prevZoneSelected = this.viewModel.selectedTimezone;
        }
        this.isBusy = false;
    }

    selectionChange(): void {
        if (this.viewModel.selectedTimezone.id === 'Other') {
            this.previousTimezone = this.prevZoneSelected;

            this.commonService.openModal(TimezoneModalComponent, { modalData: this.allTimezoneList }).then((value) => {
                if (value.modalData) {
                    this.timeZonesList.splice(-1, 1);
                    this.timeZonesList.push(value.modalData);

                    this.timeZonesList.push(new Timezone({
                        description: 'Other...',
                        id: 'Other'
                    }));

                    this.prevZoneSelected = value.modalData;
                }

                const itemIndex = _.findIndex(this.timeZonesList, {
                    id: value.modalData ? value.modalData.id : this.previousTimezone.id
                });

                this.viewModel.selectedTimezone = this.timeZonesList[itemIndex];
            });
        } else {
            this.prevZoneSelected = this.viewModel.selectedTimezone;
        }
    }

    onChange(event: any) {
        this.viewModel.model.availableProjectSetupOptions = [];

        for (let i = 0; i < this.modes.length; i++) {
            if (this.availableProjectSetupOptions[this.modes[i]]) {
                let item = this.modes[i] === AvailableProjectSetupOptions.DIGITAL_REEF_ONLY ? AvailableProjectSetupOptions.DIGITAL_REEF_ONLY :
                    this.modes[i] === AvailableProjectSetupOptions.DIGITAL_REEF_AND_RELATIVITY ? AvailableProjectSetupOptions.DIGITAL_REEF_AND_RELATIVITY :
                        AvailableProjectSetupOptions.DIGITAL_REEF_AND_REEF_REVIEW;

                this.viewModel.model.availableProjectSetupOptions.push(item);
            }
        }
        this.isDirty = this.viewModel.isDirty();
        this.setValidations(event);
    }

    openHelp = () => this.commonService.openHelp(this.helpIndex);

    saveChanges(): Promise<boolean> {
        this.isBusy = true;
        this.isBusyMessage = 'Saving Preferences...';

        if (this.settingsType === SettingsType.project) {
            return new Promise<boolean>((resolve) => {
                if (this.handle) {
                    this.restService.post(Endpoint.projectManager_updatePreference, {
                        ownerId: this.handle,
                        drTimezone: this.viewModel.selectedTimezone.id,
                        htmlConversionCacheEnabled: this.viewModel.model.htmlConversionCacheEnabled,
                        suppressDuplicates: this.viewModel.model.suppressDuplicates,
                        lastAccessTimeForArchival: this.viewModel.model.lastAccessTimeForArchival.value,
                        suppressArchivedNavTree: this.viewModel.model.suppressArchivedNavTree,
                        dashboardReportsEnabled: this.commonService.dashboardReportsEnabled,
                    }).then(response => {
                        if (response?.preferences) {
                            response.preferences.lastAccessTimeForArchival = { value: response.preferences.lastAccessTimeForArchival };
                            this.commonService.lastAccessTimeForArchival = response.preferences.lastAccessTimeForArchival;
                            this.viewModel = new PreferencesViewModel(response.preferences);
                            this.viewModel.selectedTimezone = response.preferences.drTimezone;
                            this.commonService.suppressDuplicates = response.preferences.suppressDuplicates;
                            if (this.commonService.archivalTimeForLeftTreeItems !== response.preferences.lastAccessTimeForArchival) {
                                this.commonService.archivalTimeForLeftTreeItems = response.preferences.lastAccessTimeForArchival;
                                this.commonService.sendMessage(MessageType.refreshLeftTreeNodes);
                            }
                            if (this.commonService.hideArchivedItems !== response.preferences.suppressArchivedNavTree) {
                                this.commonService.hideArchivedItems = response.preferences.suppressArchivedNavTree;
                                this.commonService.sendMessage(MessageType.refreshLeftTreeNodes, { archivedFlagChanged: true });
                            }

                            this.commonService.preferences = {
                                drTimezone: this.viewModel.selectedTimezone,
                                htmlConversionCacheEnabled: this.viewModel.model.htmlConversionCacheEnabled,
                                suppressDuplicates: this.commonService.suppressDuplicates,
                                lastAccessTimeForArchival: response.preferences.lastAccessTimeForArchival?.value ?? response.preferences.lastAccessTimeForArchival,
                                suppressArchivedNavTree: this.commonService.hideArchivedItems,
                            };
                        }

                        this.isDirty = false;
                        this.listDRTimezones();
                        this.isBusy = false;
                        resolve(true);
                    }, error => {
                        this.isBusy = false;
                        resolve(false);
                    });
                } else {
                    resolve(false);
                }
            });
        } else if (this.settingsType === SettingsType.organization) {
            this.setReefExpressSettings();
        }
    }

    discardChanges(): void {
        this.isDirty = false;
        if (this.settingsType === SettingsType.project) {
            this.listDRTimezones();
        } else if (this.settingsType === SettingsType.organization) {
            this.checkboxValidation = false;
            this.radioButtonValidation = false;
            this.getReefExpressSettings();
        }
    }

    sortTimezone(): void {
        this.timeZonesList = this.timeZonesList.sort((t1, t2): number => {
            const offset1 = t1.offsetInfo.negative ? -1 * t1.offsetInfo.hours : t1.offsetInfo.hours;
            const offset2 = t2.offsetInfo.negative ? -1 * t2.offsetInfo.hours : t2.offsetInfo.hours;
            if (offset1 > offset2) {
                return 1;
            } else if (offset1 < offset2) {
                return -1;
            }
            return 0;
        });
    }

    disabledSpace(timezone: Timezone): boolean {
        return timezone.id === '';
    }

    searchTimezones(array: Array<any>, itemToSearch: any): any {
        let index = -1;
        for (let i = 0; i < array.length; i++) {
            if (array[i].id === itemToSearch) {
                index = i;
                break;
            }
        }
        return { found: array[index], position: index };
    }

    isSaveDisabled(): boolean {
        let isDisabled = true;
        if (!this.isBusy && this.isDirty) {
            isDisabled = false;
        }
        return isDisabled;
    }

    isDiscardDisabled(): boolean {
        let isDisabled = true;
        if ((!this.isBusy && this.isDirty) || this.checkboxValidation || this.radioButtonValidation) {
            isDisabled = false;
        }
        return isDisabled;
    }

    onArchivalTimeChanged(newArchivalTime?: any): void {
        if (newArchivalTime?.value !== this.viewModel?.original?.lastAccessTimeForArchival?.value.toString()) {
            this.isDirty = true;
        } else {
            this.isDirty = false;
        }
    }

    getReefExpressSettings(): void {
        this.isBusy = true;
        this.restService.post(Endpoint.orgManager_getReefExpressSettings, { handle: this.commonService.currentOrganizationHandle }).then(response => {
            if (response) {
                const preferences = {
                    autoProjectCreation: response.autoProjectCreation,
                    defaultProcessingOption: response.defaultProcessingOption,
                    availableProjectSetupOptions: response.availableProjectSetupOptions
                }

                this.viewModel = new PreferencesViewModel(preferences);

                this.availableProjectSetupOptions[AvailableProjectSetupOptions.DIGITAL_REEF_ONLY] = false;
                this.availableProjectSetupOptions[AvailableProjectSetupOptions.DIGITAL_REEF_AND_RELATIVITY] = false;
                this.availableProjectSetupOptions[AvailableProjectSetupOptions.DIGITAL_REEF_AND_REEF_REVIEW] = false;

                if (isArray(this.viewModel.model.availableProjectSetupOptions)) {
                    this.viewModel.model.availableProjectSetupOptions.forEach(element => {
                        this.availableProjectSetupOptions[element] = this.originalAvailableProjectSetupOptions[element] = true;
                    });
                }
            }

            this.setValidations();

            this.isBusy = false;
        }, error => {
            this.isBusy = false;
        });
    }

    setReefExpressSettings(): void {
        const selectedOptions = [];
        for (const key in this.availableProjectSetupOptions) {
            if (Object.prototype.hasOwnProperty.call(this.availableProjectSetupOptions, key)) {
                if (this.availableProjectSetupOptions[key] === true) {
                    selectedOptions.push(key);
                }
            }
        };

        const payload = {
            // Here should be other payload property like organizationName or handle?
            autoProjectCreation: this.viewModel.model.autoProjectCreation,
            defaultProcessingOption: this.viewModel.model.defaultProcessingOption,
            availableProjectSetupOptions: selectedOptions
        }
        this.restService.post(Endpoint.orgManager_setReefExpressSettings, payload).then(response => {
            if (response) {
                const preferences = {
                    autoProjectCreation: response.autoProjectCreation,
                    defaultProcessingOption: response.defaultProcessingOption,
                    availableProjectSetupOptions: response.availableProjectSetupOptions
                }
                this.viewModel = new PreferencesViewModel(preferences);
            }
            this.isBusy = false;
        }, error => {
            this.isBusy = false;
        })
    }

    setValidations(event?: any) {
        this.isDROnlyOption = this.availableProjectSetupOptions['DIGITAL_REEF_ONLY'] === false;
        this.isReefAndRelativityOption = this.availableProjectSetupOptions['DIGITAL_REEF_AND_RELATIVITY'] === false;
        this.isReefAndRROption = this.availableProjectSetupOptions['DIGITAL_REEF_AND_REEF_REVIEW'] === false;

        if (this.isDROnlyOption && this.viewModel.model.autoProjectCreation === AutoProjectCreation.Digital_Reef_Only) {
            this.viewModel.model.autoProjectCreation = null;
        } else if (this.isReefAndRelativityOption && this.viewModel.model.autoProjectCreation === AutoProjectCreation.Digital_Reef_and_Relativity) {
            this.viewModel.model.autoProjectCreation = null;
        } else if (this.isReefAndRROption && this.viewModel.model.autoProjectCreation === AutoProjectCreation.Digital_Reef_and_Reef_Review) {
            this.viewModel.model.autoProjectCreation = null;
        }

        this.checkboxValidation = this.isDROnlyOption && this.isReefAndRelativityOption && this.isReefAndRROption;
        this.radioButtonValidation = this.checkboxValidation ? false : (event === AutoProjectCreation.Digital_Reef_Only || event === AutoProjectCreation.Digital_Reef_and_Relativity || event === AutoProjectCreation.Digital_Reef_and_Reef_Review) ? false
            : this.checkboxValidation || this.viewModel.model.autoProjectCreation === null ? true : false;

        let check = (this.viewModel.model.autoProjectCreation === AutoProjectCreation.Digital_Reef_Only && this.isDROnlyOption) ||
            (this.viewModel.model.autoProjectCreation === AutoProjectCreation.Digital_Reef_and_Relativity && this.isReefAndRelativityOption) ||
            (this.viewModel.model.autoProjectCreation === AutoProjectCreation.Digital_Reef_and_Reef_Review && this.isReefAndRROption);

        if (this.checkboxValidation || this.radioButtonValidation || check) {
            this.isDirty = false;
        } else {
            this.isDirty = true;
        }
    }
}